import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { analysisService } from '../../services/analysisService';
import { corpusService } from '../../services/corpusService';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { data: recentAnalyses } = useQuery({
    queryKey: ['analyses', 'recent'],
    queryFn: () => analysisService.getAnalysisHistory({ page: 1, limit: 5 }),
  });

  const { data: corpusStats } = useQuery({
    queryKey: ['corpus', 'stats'],
    queryFn: corpusService.getCorpusStats,
  });

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-blue-600 rounded-xl p-8 shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <h1 className="text-3xl font-bold mb-2 text-white">
              Hola, {user?.fullName || 'Usuario'}
            </h1>
            <p className="text-white/90">
              Herramienta profesional de análisis de lenguaje claro
            </p>
          </div>
          <Button
            onClick={() => navigate('/analysis/new')}
            className="bg-white text-blue-600 hover:bg-blue-50"
            size="lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo Análisis
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
          title="Análisis Totales"
          value={recentAnalyses?.pagination?.total || 0}
          color="blue"
          trend="+12%"
        />
        <StatsCard
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          }
          title="Palabras en Corpus"
          value={corpusStats?.stats?.total || 0}
          color="purple"
          trend="7.5K+"
        />
        <StatsCard
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
            </svg>
          }
          title="Términos en Glosario"
          value={corpusStats?.stats?.totalGlossary || 0}
          color="green"
          trend="1K+"
        />
      </div>

      {/* Recent Analyses */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Análisis Recientes</CardTitle>
              <CardDescription>Tus últimos textos analizados</CardDescription>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/history')}
              className="text-primary hover:text-primary"
            >
              Ver todos
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {recentAnalyses?.analyses?.length ? (
            <div className="space-y-3">
              {recentAnalyses.analyses.map((analysis) => (
                <Card
                  key={analysis.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/analysis/${analysis.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-foreground">
                            {analysis.title || 'Análisis sin título'}
                          </h3>
                          {analysis.sector && (
                            <Badge variant="secondary" className="shrink-0">
                              {analysis.sector.icon} {analysis.sector.name}
                            </Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                          {analysis.originalText.substring(0, 150)}...
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {new Date(analysis.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {analysis.totalWords} palabras
                          </span>
                          {analysis.fleschScore != null && (
                            <span className="inline-flex items-center gap-1.5 font-semibold text-primary">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              Flesch: {Number(analysis.fleschScore).toFixed(1)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-muted-foreground">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">No hay análisis aún</h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">
                Comienza analizando tu primer texto para evaluar su claridad y legibilidad
              </p>
              <Button onClick={() => navigate('/analysis/new')}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Crear primer análisis
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface StatsCardProps {
  icon: React.ReactNode;
  title: string;
  value: number;
  color: 'blue' | 'purple' | 'green';
  trend: string;
}

function StatsCard({ icon, title, value, color, trend }: StatsCardProps) {
  const colorClasses = {
    blue: {
      bg: 'bg-blue-500',
      badge: 'bg-blue-50 text-blue-600',
    },
    purple: {
      bg: 'bg-fuchsia-500',
      badge: 'bg-fuchsia-50 text-fuchsia-600',
    },
    green: {
      bg: 'bg-emerald-500',
      badge: 'bg-emerald-50 text-emerald-600',
    },
  };

  const classes = colorClasses[color];

  return (
    <Card className="relative hover:shadow-lg transition-all">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          {/* Icono circular */}
          <div className={`w-14 h-14 ${classes.bg} rounded-full flex items-center justify-center text-white flex-shrink-0`}>
            {icon}
          </div>
          {/* Badge de tendencia */}
          <span className={`px-2.5 py-0.5 ${classes.badge} rounded text-xs font-semibold`}>
            {trend}
          </span>
        </div>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground font-medium">{title}</p>
          <p className="text-3xl font-bold tracking-tight">{value.toLocaleString()}</p>
        </div>
      </CardContent>
    </Card>
  );
}
